import { g } from "analytics";
import { UpdateAppearance, UpdateAppearanceProperties, UpdateItemsAppearance } from "command/basic";
import { commandProcessor } from "command/command";
import { convertDemoSwatchesToRadioOptions } from "panel/shared";
import * as React from "react";
import { Articles } from "support/beacon";
import { ChemMark } from "text/parse";
import { createChemMarkGlobalConfigElements, PropertyPanelHint } from "text/ui";
import { crumb } from "utils";
/** Sentinel "indeterminate" value used for a group of radio buttons. A random string unlikely to be used as an actual value. */
export const SENTINEL_INDETERMINATE_RADIO = "@_x/.!";
/** Text to display as placeholder for text inputs with indeterminate values. */
export const SENTINEL_TEXT_PLACEHOLDER = "Multiple values";
function createUpdateCommand(items, key, newValue) {
    if (items.length > 1) {
        const values = items.map(i => ({ id: i.id, value: newValue, previousValue: i.appearance[key] }));
        return new UpdateItemsAppearance(key, values);
    }
    else {
        const item = items[0];
        return new UpdateAppearance(item.id, key, newValue, item.appearance[key]);
    }
}
function getConsistentValue(items, key) {
    const value = items[0].appearance[key];
    if (items.length == 1)
        return value;
    for (const item of items) {
        if (item.appearance[key] !== value)
            return;
    }
    return value;
}
export function convertAppearanceSpecs(specs, item, items, app, tiers) {
    return specs.filter(spec => { var _a; return ((_a = spec.visible) !== null && _a !== void 0 ? _a : (a => true))(item.appearance); }) // Filter out specs set to invisible, if visibility fn is defined.
        .map(spec => {
        var _a, _b, _c;
        switch (spec.spec) {
            case "slider":
                // Create a slider.
                // Sliders are not reusable and so need a unique key.
                return Object.assign(Object.assign({}, spec), { spec: "slider", key: `${item.type}.${spec.key}`, initialValue: item.appearance[spec.key], onStart: () => { app.setIsInteracting(true); }, onChange: value => {
                        var _a;
                        for (const item of items) {
                            (_a = app.getApparatusComponent(item.id)) === null || _a === void 0 ? void 0 : _a.updateAppearanceAttributes(spec.key, value);
                        }
                    }, onEnd: (_, final) => {
                        app.setIsInteracting(false);
                        g("UpdateAppearance", `${item.type}::${spec.key}`);
                        crumb("ui.props", `${item.type}: ${spec.key} -> ${final}`);
                        commandProcessor.execute(createUpdateCommand(items, spec.key, final));
                    } });
            case "radio": {
                // Create a group of radio buttons.
                const currentValue = (_a = getConsistentValue(items, spec.key)) !== null && _a !== void 0 ? _a : SENTINEL_INDETERMINATE_RADIO;
                // Filter options to only those that are made visible.
                const options = spec.options.filter(o => o.visible === undefined || o.visible(item.appearance));
                return Object.assign(Object.assign({}, spec), { options, id: spec.key, value: currentValue, onChange: (_, final) => {
                        g("UpdateAppearance", `${item.type}::${spec.key}`);
                        crumb("ui.props", `${item.type}: ${spec.key} -> ${final}`);
                        commandProcessor.execute(createUpdateCommand(items, spec.key, final));
                    } });
            }
            case "select":
                // Create a group of radio buttons.
                return Object.assign(Object.assign({}, spec), { id: spec.key, value: item.appearance[spec.key], onSelectChange: (_, final) => {
                        crumb("ui.props", `${item.type}: ${spec.key} -> ${final}`);
                        commandProcessor.execute(createUpdateCommand(items, spec.key, final));
                    } });
            case "toggle-buttons": {
                // Identify the highlight index.
                const currentValue = getConsistentValue(items, spec.key);
                var highlightIndex = -1;
                if (currentValue !== undefined) {
                    for (let i = 0; i < spec.options.length; i++) {
                        if (spec.options[i].value == currentValue) {
                            highlightIndex = i;
                            break;
                        }
                    }
                }
                return {
                    spec: "toggle-horizontal-buttons",
                    id: spec.key,
                    key: `${item.id.id}_${spec.key}`,
                    label: spec.label,
                    highlightIndex: highlightIndex,
                    size: spec.size,
                    buttonsPerRow: spec.buttonsPerRow,
                    allowSelectingTierLockedOptions: spec.allowSelectingTierLockedOptions,
                    buttons: spec.size === "text" ? spec.options.map(o => ({
                        icon: "",
                        buttonLabel: o.label,
                        hint: o.hint,
                        tier: o.tier,
                        onClick: () => {
                            crumb("ui.props", `${item.type}: ${spec.key} -> ${o.value}`);
                            commandProcessor.execute(createUpdateCommand(items, spec.key, o.value));
                        }
                    })) : spec.options.map(o => ({
                        icon: o.icon,
                        buttonLabel: o.label,
                        hint: o.hint,
                        tier: o.tier,
                        onClick: () => {
                            crumb("ui.props", `${item.type}: ${spec.key} -> ${o.value}`);
                            commandProcessor.execute(createUpdateCommand(items, spec.key, o.value));
                        }
                    }))
                };
            }
            case "text": {
                const currentValue = getConsistentValue(items, spec.key);
                return Object.assign(Object.assign({}, spec), { key: `${item.id.id}_${spec.key}_${currentValue}`, spec: "text", id: spec.key, text: currentValue !== null && currentValue !== void 0 ? currentValue : "", placeholder: SENTINEL_TEXT_PLACEHOLDER.if(currentValue === undefined), onFocus: () => { app.setIsEditingText(true); }, onChange: value => {
                        var _a;
                        for (const item of items) {
                            (_a = app.getApparatusComponent(item.id)) === null || _a === void 0 ? void 0 : _a.updateAppearanceAttributes(spec.key, value);
                        }
                    }, onEnd: (previous, final) => {
                        app.setIsEditingText(false);
                        if (previous == final)
                            return;
                        g("UpdateAppearance", `${item.type}::${spec.key}`);
                        crumb("ui.props", `${item.type}: ${spec.key} -> ${final}`);
                        commandProcessor.execute(createUpdateCommand(items, spec.key, final));
                    } });
            }
            case "textarea":
                const currentValue = getConsistentValue(items, spec.key);
                return Object.assign(Object.assign({}, spec), { key: `${item.id.id}_${spec.key}_${currentValue}`, spec: "textarea", id: spec.key, text: currentValue !== null && currentValue !== void 0 ? currentValue : "", placeholder: SENTINEL_TEXT_PLACEHOLDER.if(currentValue === undefined), onFocus: () => { app.setIsEditingText(true); }, onChange: value => {
                        var _a;
                        for (const item of items) {
                            (_a = app.getApparatusComponent(item.id)) === null || _a === void 0 ? void 0 : _a.updateAppearanceAttributes(spec.key, value);
                        }
                    }, onEnd: (previous, final) => {
                        app.setIsEditingText(false);
                        if (previous == final)
                            return;
                        g("UpdateAppearance", `${item.type}::${spec.key}`);
                        crumb("ui.props", `${item.type}: ${spec.key} -> ${final}`);
                        commandProcessor.execute(createUpdateCommand(items, spec.key, final));
                    } });
            case "color":
                let startColor = item.appearance[spec.key];
                // Depending on whether this is supported or not on the
                // current plan, return either a full color picker
                // or a limited radio list from demo swatches.
                if (!tiers.allow(spec) && spec.demoSwatches) {
                    // Return a limited set of choices as radios.
                    return {
                        spec: "radio",
                        id: spec.key,
                        key: spec.key,
                        label: spec.label,
                        visible: spec.visible,
                        options: convertDemoSwatchesToRadioOptions(spec.demoSwatches),
                        value: startColor,
                        onChange: (_, final) => {
                            crumb("ui.props", `${item.type}: ${spec.key} -> ${final}`);
                            commandProcessor.execute(createUpdateCommand(items, spec.key, final));
                        }
                    };
                }
                // Return a normal color picker.
                return Object.assign(Object.assign({}, spec), { id: spec.key, 
                    // Colorpicker needs a unique key per instance.
                    key: spec.key + "_" + item.id.id, value: startColor, onColorChange: value => {
                        var _a;
                        for (const item of items) {
                            // TODO: Fix this bug where component may be null
                            (_a = app.getApparatusComponent(item.id)) === null || _a === void 0 ? void 0 : _a.updateAppearanceAttributes(spec.key, value);
                        }
                    }, onColorEnd: (final) => {
                        if (final == startColor)
                            return;
                        g("UpdateAppearance", `${item.type}::${spec.key}`);
                        crumb("ui.props", `${item.type}: ${spec.key} -> ${final}`);
                        commandProcessor.execute(createUpdateCommand(items, spec.key, final));
                    }, opacity: spec.opacity
                        ? opacitySettingsForColorpicker(items, app, spec.opacity)
                        : undefined });
            case "checkbox":
                /** Convert to a checkbox-group with 1 check box. */
                const cg = {
                    spec: "checkbox-group",
                    label: spec.label,
                    key: spec.key,
                    checkboxes: [
                        Object.assign(Object.assign({}, spec), { id: spec.key, label: (_b = spec.subLabel) !== null && _b !== void 0 ? _b : "", value: getConsistentValue(items, spec.key), useSwitch: spec.useSwitch, onCheckedChange: newValue => {
                                g("UpdateAppearance", `${item.type}::${spec.key}`);
                                crumb("ui.props", `${item.type}: ${spec.key} -> ${newValue}`);
                                commandProcessor.execute(createUpdateCommand(items, spec.key, newValue));
                            } }),
                    ]
                };
                return cg;
            case "checkbox-group": {
                const checkboxSpecs = spec.checkboxes
                    .filter(cSpec => { var _a; return ((_a = cSpec.visible) !== null && _a !== void 0 ? _a : (a => true))(item.appearance); })
                    .map((cSpec) => (Object.assign(Object.assign({}, cSpec), { id: cSpec.key, value: getConsistentValue(items, cSpec.key), useSwitch: cSpec.useSwitch, onCheckedChange: newValue => {
                        g("UpdateAppearance", `${item.type}::${cSpec.key}`);
                        crumb("ui.props", `${item.type}: ${cSpec.key} -> ${newValue}`);
                        commandProcessor.execute(createUpdateCommand(items, cSpec.key, newValue));
                    } })));
                if (checkboxSpecs.length == 0)
                    return React.createElement("div", { key: `${spec.label}_empty` });
                return Object.assign(Object.assign({}, spec), { key: spec.label, visible: checkboxSpecs.length > 0, checkboxes: checkboxSpecs });
            }
            case "header": {
                const toggleKey = spec.toggleKey;
                return Object.assign(Object.assign({ key: "__fakeKey" + spec.header, label: "" }, spec), { toggle: toggleKey
                        ? {
                            id: toggleKey,
                            value: getConsistentValue(items, toggleKey),
                            useSwitch: true,
                            onCheckedChange: newValue => {
                                g("UpdateAppearance", `${item.type}::${toggleKey}`);
                                crumb("ui.props", `${item.type}: ${toggleKey} -> ${newValue}`);
                                commandProcessor.execute(createUpdateCommand(items, toggleKey, newValue));
                            }
                        }
                        : undefined });
            }
            case "button": {
                return {
                    spec: "button",
                    key: spec.label,
                    label: "",
                    buttonLabel: spec.label,
                    onClick: () => {
                        // TODO: Support this for multiple items.
                        const newValues = spec.onClick(item.appearance);
                        const prevValues = {};
                        for (const key in newValues) {
                            prevValues[key] = item.appearance[key];
                        }
                        const command = new UpdateAppearanceProperties(item.id, newValues, prevValues);
                        commandProcessor.execute(command);
                    }
                };
            }
            case "color-wells": {
                const currentValue = (_c = getConsistentValue(items, spec.key)) !== null && _c !== void 0 ? _c : undefined;
                return Object.assign(Object.assign({}, spec), { value: currentValue, onColorSelected: (color) => {
                        commandProcessor.execute(createUpdateCommand(items, spec.key, color));
                    } });
            }
            case "array":
                return {
                    spec: "text",
                    id: spec.key,
                    key: spec.key,
                    label: "Hello",
                    text: "NOT WORKING",
                    onChange: value => {
                    },
                    onEnd: (previous, final) => {
                    }
                };
        }
    });
}
function opacitySettingsForColorpicker(items, app, spec) {
    return {
        value: items[0].appearance[spec.key],
        range: spec.range,
        onChange: value => {
            var _a;
            for (const item of items) {
                (_a = app.getApparatusComponent(item.id)) === null || _a === void 0 ? void 0 : _a.updateAppearanceAttributes(spec.key, value);
            }
        },
        onEnd: (_, final) => {
            g("UpdateAppearance", `${items[0].type}::${spec.key}`);
            commandProcessor.execute(createUpdateCommand(items, spec.key, final));
        }
    };
}
export function chemMarkPropsSection(items, key) {
    return {
        header: "ChemText",
        icon: ChemMark.faIconName,
        helpArticleId: Articles.CHEMTEXT,
        elements: [
            {
                spec: "checkbox-group",
                key: "is_chem_mark_enabled",
                label: ChemMark.uiDescription(),
                wideLabel: true,
                checkboxes: [{
                        label: "",
                        useSwitch: true,
                        id: "is_chem_mark_enabled",
                        value: items[0].appearance[key],
                        tier: "boost",
                        allowWhenLocked: true,
                        onCheckedChange: value => {
                            g("UpdateAppearance", `${items[0].type}::ChemMark`);
                            commandProcessor.execute(createUpdateCommand(items, key, value));
                        }
                    }],
            },
            React.createElement(PropertyPanelHint, { key: "chemmark_hint", showUpgradeHint: items[0].appearance[key] }),
            ...createChemMarkGlobalConfigElements(items[0].appearance[key])
        ]
    };
}
